import { Injectable } from '@angular/core';
import { OtpEventArgs } from '../../dataobject/otp-event-args';
import { ApplicationResponse } from '../../api/v4/models/Models/v4/Application/application-response';
import { PublicPersonalApplicationService } from '../../api/v4/services/public-personal-application.service';
import { Inject } from '@angular/core';
import { ErrorService } from '../error.service';
import { PublicApplicationStartRequest } from '../../api/v4/models/Models/v4/Application/Public/public-application-start-request';
import { environment } from "../../../environments/environment";
import { LoanTypeEntityForm, LoanTypeForm, LoanTypeIndividualForm, SummaryReviewForm, PersonalAboutForm, PersonalApplicationForm, PersonalLoanPurposeForm } from '../../types/v4/personal-application-types';
import { FormGroup, FormControl, Validators, FormArray, FormBuilder, ValidatorFn, ValidationErrors, AbstractControl } from '@angular/forms';
import { UserAction } from '../../api/v4/models/CommonUtilities/user-action';
import { PersonalApplicationUpdateResponse } from '../../types/v4/personal-model-aliases';
import { amountFrequencyGreaterThanZeroValidator, conditionalRequiredValidator, conditionalRequiredValidatorTraverseFg, mustBeTrueValidator, numericRangeValidator } from '../../types/validators';
import { LoanEntityType } from '../../api/v4/models/CommonUtilities/loan-entity-type';
import { IndividualRequest } from '../../api/v4/models/Models/v4/Application/individual-request';
import { ApplicationConfigResponse } from '../../api/v4/models/Models/v4/Application/application-config-response';
import { AddressForm, CreditFacilityForm, ExpenseDetailForm, FinanceCommitmentForm, IndividualCommonDataForm, LiabilitiesForm, LivingExpensesForm, PersonalIndividualForm } from '../../types/v4/personal-individual-steps';
import { MessageService } from '../message.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { AddressRequest } from '../../api/v4/models/Models/v4/Application/PersonalDetails/address-request';
import { ContactTypeEnum } from '../../api/v4/models/Models/v4/Application/contact-type-enum';
import { LoanInformationBaseRequestPeriodTypeEnum } from '../../api/v4/models/Models/v4/Application/LoanInformation/loan-information-base-request-period-type-enum';
import { LoanPurposeEnum } from '../../api/v4/models/Models/v4/Application/LoanInformation/loan-purpose-enum';
import { PeriodTypeEnum } from '../../api/v4/models/Models/Application/FinancialDetails/period-type-enum';
import { ApplicationExpenseType } from '../../api/v4/models/CommonUtilities/application-expense-type';
import { PublicPaymentDetailApplicationExpenseType } from '../../api/v4/models/Models/v4/Application/Public/Financial/public-payment-detail-application-expense-type';
import { MaritalStatusEnum } from '../../api/v4/models/Models/v4/Application/PersonalDetails/marital-status-enum';

const uniqueLoanPurposesValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const purposes = (control.value as Array<{ purpose?: LoanPurposeEnum | null }> | null) ?? [];
  const selectedPurposes = purposes.map(row => row.purpose).filter((purpose): purpose is LoanPurposeEnum => purpose != null);
  return new Set(selectedPurposes).size === selectedPurposes.length
    ? null
    : { duplicateLoanPurpose: true };
};

@Injectable({
  providedIn: 'root'
})
export class PersonalAppService {

  private entityTypeSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public entityType$: Observable<string> = this.entityTypeSubject.asObservable();

  private individualTypeSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public individualType$: Observable<string> = this.individualTypeSubject.asObservable();

  private primaryIndividualSubject = new BehaviorSubject<FormGroup<PersonalIndividualForm> | null>(null);
  public indivIndividualSubject$ = this.primaryIndividualSubject.asObservable();

  otpEventArgs!: OtpEventArgs;
  newApplicationResponse: ApplicationResponse = {};
  isVerified: boolean = false;

  personalApplicationFg: PersonalApplicationForm | null = null;
  public stepperDuration: string = '200';

  loanApplicationResponse: PersonalApplicationUpdateResponse = {
    loanInformation: {
      isBusinessLoan: UserAction.Unspecified,
      loanAmount: 0,
      loanTermMonths: 36,
      loanEntityType: LoanEntityType.Individual,
      loanPurpose: LoanPurposeEnum.DebtConsolidation,

    }
  };

  //Default to virutal 9 steps (only actually 3 steps until an individual is added)
  public totalSteps: number = 10;

  currentStep: number = 1;

  public entityTypeDisplay: string = 'Person';

  get percentageStepsComplete(): number {
    return (this.currentStep / this.totalSteps) * 100;
  }

  public setTotalStepsActual(): void {
    this.totalSteps = 3;
  }

  public setCurrentStep(stepIndex: number): void {
    this.currentStep = stepIndex;
  }

  public ApplicationConfigResponse: ApplicationConfigResponse = {};


  constructor(
    @Inject(PublicPersonalApplicationService) private PublicPersonalApplicationService: PublicPersonalApplicationService,
    private errorService: ErrorService,
    private formBuilder: FormBuilder,
    private messageService: MessageService
  ) {

    // Debug mode
    if (environment.bypassOtpVerification) {
      if (environment.apiUrl.includes('localhost')) {
        this.newApplicationResponse.publicId = '1C732738AD2744E08BB709843E3A9E2F';
      }
      else if (environment.apiUrl.includes('api-integration')) {
        this.newApplicationResponse.publicId = '87edd2b40e6548fc847d7a70bdb11b78';
      }
      this.isVerified = true;

      let firstname = 'Testfirstname';
      let lastname = 'Testlastname';
      let mobile = '021111111';

      this.loanApplicationResponse.applicants = {
        individuals: [{
          customerDetails: {
            firstName: firstname,
            lastName: lastname,
            applicantType: ContactTypeEnum.Primary,
            mobileNumber: mobile,
            email: 'test@motorcentral.co.nz',
          },
        }]
      }

      this.otpEventArgs = new OtpEventArgs();
      this.otpEventArgs.firstName = firstname;
      this.otpEventArgs.lastName = lastname;
      this.otpEventArgs.mobile = mobile;

      this.personalApplicationFg = this.buildPersonalApplicationFg(this.loanApplicationResponse);
      this.buildTestData();
    }
  }


  buildTestData() {
    if (this.personalApplicationFg) {
      this.personalApplicationFg.aboutFg.controls.loanPurposes.at(0).setValue({
        purpose: LoanPurposeEnum.DebtConsolidation,
        amount: 10000
      });
      this.personalApplicationFg.loanTypeFg.controls.loanEntityType.setValue(LoanEntityType.Individual);
      this.personalApplicationFg.loanTypeFg.controls.hasAdditionalBorrowers.setValue(UserAction.Yes);

      this.personalApplicationFg.loanTypeFg.controls.individuals.push(this.buildIndividualForm(
        {
          customerDetails: {
            firstName: 'Testfirstname',
            lastName: 'Testlastname',
            applicantType: ContactTypeEnum.Primary,
            mobileNumber: '021111111',
            email: 'test@motorcentral.co.nz '
          }
        }
      ));

    }
  }

  moveCurrentStepCountBack() {

    if (this.currentStep > 0) {
      this.currentStep -= 1;
    }
    console.log('moveCurrentStepCountBack', this.currentStep);
    console.log('total steps', this.totalSteps);
  }

  moveCurrentStepCountForward() {
    this.currentStep += 1;
    console.log('moveCurrentStepCountForward', this.currentStep);
    console.log('total steps', this.totalSteps);
  }

  verifyInitIdentity(otpEventArgs: OtpEventArgs): Promise<boolean> {
    this.otpEventArgs = otpEventArgs;

    const urlParams = new URLSearchParams(window.location.search);
    const publicId = urlParams.get('publicId');
    const dealershipCode = urlParams.get('dealershipCode');
    const origin = urlParams.get('origin');
    const stockNo = urlParams.get('stockNo');
    const externalListingId = urlParams.get('listingId');

    return new Promise((resolve, reject) => {
      this.PublicPersonalApplicationService.v4PublicApplicationPersonalPost$Json$Json({
        body: {
          details: {
            publicAccountId: publicId,
            branchCode: dealershipCode,
            otpDetails: {
              clientNonce: otpEventArgs.otpNonce,
              email: otpEventArgs.email,
              mobile: otpEventArgs.mobile,
              name: otpEventArgs.firstName,
              otpHash: otpEventArgs.otpHash,
              otp: otpEventArgs.otpCode,
            },
            locationSource: origin ? origin : '',
            stockNo: stockNo,
            externalListingId: externalListingId ? externalListingId : '',
            lastName: otpEventArgs.lastName,
          }
        } as PublicApplicationStartRequest
      })
        .subscribe({
          next: (response) => {
            this.newApplicationResponse = response.entityData!;
            this.isVerified = true;
            console.log(response);

            this.loanApplicationResponse.applicants = {
              individuals: [{
                customerDetails: {
                  firstName: this.otpEventArgs.firstName,
                  lastName: this.otpEventArgs.lastName,
                  applicantType: ContactTypeEnum.Primary,
                  mobileNumber: this.otpEventArgs.mobile,
                  email: this.otpEventArgs.email != '' ? this.otpEventArgs.email : null,
                }
              }]
            }

            this.personalApplicationFg = this.buildPersonalApplicationFg(this.loanApplicationResponse);

            if (environment.debug) {
              this.buildTestData();
            }
            resolve(true);
          },
          error: (errorResponse) => {
            this.errorService.handleError(errorResponse);
            this.messageService.setLoadingState(false);
            reject(false);
          }
        });
    });
  }

  buildPersonalApplicationFg(app: PersonalApplicationUpdateResponse): PersonalApplicationForm {
    const form: PersonalApplicationForm = {
      aboutFg: this.buildAboutFg(this.loanApplicationResponse),
      loanTypeFg: this.buildLoanTypeFg(this.loanApplicationResponse),
      indivFormArray: null,
      livingExpensesFg: this.buildLivingExpensesFg(this.loanApplicationResponse),
      liabilitiesFg: this.buildLiabilitiesFg(this.loanApplicationResponse),
      summaryReviewFg: this.buildSummaryReviewFg(this.loanApplicationResponse),
      entityFg: null,
    };

    return form;
  }

  /**
  * Call this method whenever indivFormArray is set or updated.
  */
  public notifyIndivFormArray(indivForm: FormGroup<PersonalIndividualForm> | null) {
    this.primaryIndividualSubject.next(indivForm);
  }

  buildSummaryReviewFg(app: PersonalApplicationUpdateResponse): FormGroup<SummaryReviewForm> {
    return this.formBuilder.group<SummaryReviewForm>({
      hasFutureUpcomingChanges: new FormControl(UserAction.Unspecified),
      futureUpcomingChangesDescription: new FormControl(''),
    });
  }

  buildAboutFg(app: PersonalApplicationUpdateResponse): FormGroup<PersonalAboutForm> {
    const loanPurposes = this.formBuilder.array([
        this.formBuilder.group<PersonalLoanPurposeForm>({
          purpose: new FormControl(null, Validators.required),
          amount: new FormControl(null, [Validators.required, numericRangeValidator(1, 150000)])
        })
      ], { validators: uniqueLoanPurposesValidator });
    const loanPurposeOther = new FormControl<string | null>(null, control => {
      const purposes = loanPurposes.controls.map(row => row.controls.purpose.value);
      return purposes.includes(LoanPurposeEnum.Other) && !control.value?.trim()
        ? { required: true }
        : null;
    });

    loanPurposes.valueChanges.subscribe(() => loanPurposeOther.updateValueAndValidity({ emitEvent: false }));

    return this.formBuilder.group<PersonalAboutForm>({
      loanPurposes,
      loanPurposeOther,
      includeMaxBorrowAmount: new FormControl(false),
      preferredLoanTerm: new FormControl(60, Validators.required),
      repaymentFrequency: new FormControl(LoanInformationBaseRequestPeriodTypeEnum.Weekly, Validators.required)
    });
  }

  buildLoanTypeFg(app: PersonalApplicationUpdateResponse): FormGroup<LoanTypeForm> {
    var loanType = this.formBuilder.group<LoanTypeForm>({
      loanEntityType: new FormControl(LoanEntityType.Individual, Validators.required),
      isExistingCustomer: new FormControl(null, Validators.required),
      isTopUp: new FormControl(null, Validators.required),
      individuals: this.buildIndividualFormArray(app),
      entities: this.formBuilder.array<FormGroup<LoanTypeEntityForm>>([]),
      hasAdditionalBorrowers: new FormControl(null, Validators.required)
    });

    return loanType;
  }

  buildLivingExpensesFg(app: PersonalApplicationUpdateResponse): FormGroup<LivingExpensesForm> {
    var fg = this.formBuilder.group<LivingExpensesForm>({
      understandsBankStatReq: new FormControl(null, mustBeTrueValidator),
      hasOtherExpenses: new FormControl(null, Validators.required),
      otherExpenses: this.formBuilder.array<FormGroup<ExpenseDetailForm>>([]),
      expensesSharedWithPartner: new FormControl(null, Validators.required),
      partnerNetIncome: new FormControl({ value: null, disabled: true }, Validators.compose([conditionalRequiredValidator('expensesSharedWithPartner', UserAction.Yes), numericRangeValidator(-1, 10000000)])),
      partnerNetIncomeFrequency: new FormControl<PeriodTypeEnum | null>(null, amountFrequencyGreaterThanZeroValidator('partnerNetIncome', 'partnerNetIncomeFrequency')),

      hasNonSharedPartnerExpenses: new FormControl(null),
      billsUtilitiesExpense: this.createExpenseGroup(PublicPaymentDetailApplicationExpenseType.Utilities),
      foodGroceriesExpense: this.createExpenseGroup(PublicPaymentDetailApplicationExpenseType.Groceries),
      transportExpense: this.createExpenseGroup(PublicPaymentDetailApplicationExpenseType.Transport),
      phoneInternetExpense: this.createExpenseGroup(PublicPaymentDetailApplicationExpenseType.Entertainment),
      clothingShoesPersonalCareExpense: this.createExpenseGroup(PublicPaymentDetailApplicationExpenseType.ClothingShoesPersonalCare),
      insuranceExpense: this.createExpenseGroup(PublicPaymentDetailApplicationExpenseType.Insurance),

      //hasNonSharedPartnerExpenses: new FormControl(null, conditionalRequiredValidatorMultiParentDependent('maritalStatus', [MaritalStatus.Married, MaritalStatus.DeFacto], 'isPartnerOnApplication', [UserAction.Yes])),
    });

    return fg;
  }

  createExpenseGroup(expenseType: PublicPaymentDetailApplicationExpenseType | null): FormGroup<ExpenseDetailForm> {
    var idFg = new FormGroup<ExpenseDetailForm>({
      type: new FormControl<PublicPaymentDetailApplicationExpenseType | null>(expenseType, Validators.required),
      amount: new FormControl<number | null>(null),
      frequency: new FormControl<PeriodTypeEnum | null>(null, amountFrequencyGreaterThanZeroValidator('amount', 'frequency')),
      description: new FormControl<string | null>(null)
    } as ExpenseDetailForm);

    return idFg;
  }


  buildLiabilitiesFg(app: PersonalApplicationUpdateResponse): FormGroup<LiabilitiesForm> {
    var fg = this.formBuilder.group<LiabilitiesForm>({
      hasCreditFacilities: new FormControl(null, Validators.required),
      hasOtherFinancialCommitments: new FormControl(null, Validators.required),
      creditFacilities: this.formBuilder.array<FormGroup<CreditFacilityForm>>([]),
      financeCommitments: this.formBuilder.array<FormGroup<FinanceCommitmentForm>>([]),
    });

    return fg;
  }

  buildIndividualFormArray(app: PersonalApplicationUpdateResponse): FormArray<FormGroup<LoanTypeIndividualForm>> {

    var formArray = this.formBuilder.array<FormGroup<LoanTypeIndividualForm>>([]);
    if (app.applicants?.individuals && app.applicants?.individuals?.length > 0) {
      app.applicants.individuals.forEach(individual => {
        formArray.push(this.buildIndividualForm(individual));
      });
    }
    return formArray;
  }

  buildIndividualForm(individual: IndividualRequest): FormGroup<LoanTypeIndividualForm> {
    return this.formBuilder.group<LoanTypeIndividualForm>({
      firstName: new FormControl(individual.customerDetails!.firstName!, Validators.required),
      middleName: new FormControl(''),
      lastName: new FormControl(individual.customerDetails!.lastName!, Validators.required),
      contactType: new FormControl(individual.customerDetails?.applicantType!, Validators.required),
      mobile: new FormControl(''),
      email: new FormControl(''),
      birthDate: new FormControl(''),
      directorIndex: new FormControl(-1),
    });
  }

  public buildIndividualHeaderInfo(indivCaption: string, individualFg: FormGroup<PersonalIndividualForm> | null): string {
    if (individualFg) {
      if (indivCaption == '') {
        indivCaption = 'Person';
      }

      if (individualFg) {
        var applicantIndividualNumber = this.personalApplicationFg!.indivFormArray!.controls.indexOf(individualFg) + 1;
        return `${indivCaption} ${applicantIndividualNumber}:`;
      }
    }

    return '';
  }

  setEntityType(entityType: LoanEntityType | null): void {
    this.setEntityTypeCaption(entityType);
    this.setIndividualTypeCaption(entityType);
  }

  private setEntityTypeCaption(entityType: LoanEntityType | null): void {
    let value = '';
    switch (entityType) {
      case LoanEntityType.Company:
        value = 'Company';
        break;
      case LoanEntityType.Trust:
        value = 'Trust';
        break;
      case LoanEntityType.Other:
        value = 'Entity';
        break;
    }

    this.entityTypeSubject.next(value);
  }

  private setIndividualTypeCaption(entityType: LoanEntityType | null): void {

    var entityTypeDisplay = '';
    switch (entityType) {
      case LoanEntityType.Individual:
        entityTypeDisplay = 'Person';
        // if(individualFg.controls.step1.controls.contactTypeEnum.value == ContactTypeEnum.Primary){
        // }else if(individualFg.controls.step1.controls.contactTypeEnum.value == ContactTypeEnum.Secondary){
        //   entityTypeDisplay = 'Guarantor';
        // }
        break;
      case LoanEntityType.Company:
        entityTypeDisplay = 'Guarantor';
        break;
      case LoanEntityType.Other:
      case LoanEntityType.SoleTrader:
      case LoanEntityType.Partner:
        entityTypeDisplay = 'Applicant';
        break;
      case LoanEntityType.Trust:
        entityTypeDisplay = 'Trustee';
        break;
    }

    this.individualTypeSubject.next(entityTypeDisplay);
  }

  // public getLoanEntityTypeDisplay(loanEntityType: LoanEntityType): string {
  //   var entityTypeDisplay = '';
  //   switch (loanEntityType) {
  //     case LoanEntityType.Individual:
  //       entityTypeDisplay = 'Person';
  //       // if(individualFg.controls.step1.controls.contactTypeEnum.value == ContactTypeEnum.Primary){
  //       // }else if(individualFg.controls.step1.controls.contactTypeEnum.value == ContactTypeEnum.Secondary){
  //       //   entityTypeDisplay = 'Guarantor';
  //       // }
  //       break;
  //     case LoanEntityType.Other:
  //     case LoanEntityType.Company:
  //       entityTypeDisplay = 'Guarantor';
  //       break;
  //     case LoanEntityType.SoleTrader:
  //       entityTypeDisplay = 'Applicant';
  //       break;
  //     case LoanEntityType.Partner:
  //       entityTypeDisplay = 'Applicant';
  //       break;
  //     case LoanEntityType.Trust:
  //       entityTypeDisplay = 'Trustee';
  //       break;
  //   }
  //   this.entityTypeDisplay = entityTypeDisplay;
  //   return entityTypeDisplay;
  // }

  public getDependentPartnerFg(currentIndividualFg: FormGroup<PersonalIndividualForm>, partnerId: string): FormGroup<PersonalIndividualForm> | null {
    // get the index of the individualFg from this.personalApplicationFg?.indivFormArray.controls
    var partnerIndex = this.personalApplicationFg!.indivFormArray!.controls.findIndex(x => x.controls.common.controls.id.value?.toString() == partnerId);
    var partnerFg = this.personalApplicationFg!.indivFormArray!.controls[partnerIndex] as FormGroup<PersonalIndividualForm>;

    if (partnerFg) {
      // Check to see if the current individual is before the partner in the array
      // This means the partner is a dependent of the current individual
      var currentIndividualId = this.personalApplicationFg!.indivFormArray!.controls.findIndex(x => x == currentIndividualFg);
      if (currentIndividualId < partnerIndex && currentIndividualFg.controls.step2.controls.isPartnerOnApplication.value == UserAction.Yes) {
        return partnerFg;
      }
    }

    return null;
  }

  public getParentPartnerFg(currentIndividualFg: FormGroup<PersonalIndividualForm>, partnerId: string): FormGroup<PersonalIndividualForm> | null {

    // get the index of the individualFg from this.personalApplicationFg?.indivFormArray.controls
    var partnerIndex = this.personalApplicationFg!.indivFormArray!.controls.findIndex(x => x.controls.common.controls.id.value?.toString() == partnerId);
    var partnerFg = this.personalApplicationFg!.indivFormArray!.controls[partnerIndex] as FormGroup<PersonalIndividualForm>;

    if (partnerFg) {
      // Check to see if the current individual is before the partner in the array
      // This means the partner is a dependent of the current individual
      var currentIndividualId = this.personalApplicationFg!.indivFormArray!.controls.findIndex(x => x == currentIndividualFg);
      if (currentIndividualId > partnerIndex) {
        return partnerFg;
      }
    }

    return null;
  }

  // public getIndividualPartnerFormGroup(currentIndividualFg: FormGroup<PersonalIndividualForm>, partnerId: string): FormGroup<PersonalIndividualForm> | null {
  //   // get the index of the individualFg from this.personalApplicationFg?.indivFormArray.controls
  //   var partnerIndex = this.personalApplicationFg!.indivFormArray!.controls.findIndex(x => x.controls.common.controls.id.value?.toString() == partnerId);
  //   var partnerFg = this.personalApplicationFg!.indivFormArray!.controls[partnerIndex] as FormGroup<PersonalIndividualForm>;
  //   if (partnerFg) {

  //     debugger;

  //     // get the index of currentIndividualFg from this.personalApplicationFg?.indivFormArray.controls

  //     var linkedIndividualId = this.personalApplicationFg!.indivFormArray!.controls.findIndex(x => x == currentIndividualFg);


  //     var linkedIndividualId = this.personalApplicationFg!.indivFormArray!.controls.findIndex(x => x.controls.step2.controls.partnerIdentifier.value?.toString() == partnerId &&
  //       x.controls.step2.controls.isPartnerOnApplication.value == UserAction.Yes);

  //     if (linkedIndividualId >= 0) {
  //       return partnerFg;
  //     }
  //   }
  //   return null;
  // }

  getApplicationFg(): PersonalApplicationForm {
    return this.personalApplicationFg!;
  }

  isJointApplication(): boolean {
    return this.personalApplicationFg?.loanTypeFg.controls.hasAdditionalBorrowers.value == UserAction.Yes &&
      this.personalApplicationFg.indivFormArray != null &&
      this.personalApplicationFg.indivFormArray.length > 1;
  }

  isCommericial(): boolean {
    return this.personalApplicationFg?.loanTypeFg.controls.loanEntityType.value == LoanEntityType.Company;
  }

  getIndividualIndex(individualFg: FormGroup<PersonalIndividualForm>): number {
    return this.personalApplicationFg!.indivFormArray!.controls.findIndex(x => x == individualFg);
  }
  
  isPrimaryIndividualMarried(): boolean {
    var maritalStatuses = [MaritalStatusEnum.DeFacto, MaritalStatusEnum.Married];
    const maritalStatusValue = this.personalApplicationFg?.indivFormArray?.controls[0].controls.step2.controls.maritalStatus.value;

    return this.personalApplicationFg?.indivFormArray != null &&
      maritalStatusValue != null &&
      maritalStatuses.includes(maritalStatusValue);
  }

  buildAddressFg(address: AddressRequest, durationRequired: boolean = false, livingSituationRequired: boolean = false): FormGroup<AddressForm> {

    var durationValidators = durationRequired ? [Validators.required] : [];
    var livingSituationValidators = livingSituationRequired ? [Validators.required] : [];

    return this.formBuilder.group<AddressForm>({
      isManualEdit: new FormControl(false),
      caption: new FormControl('', conditionalRequiredValidator('isManualEdit', false)),
      street: new FormControl('', Validators.required),
      streetNo: new FormControl('', Validators.required),
      city: new FormControl('', Validators.required),
      suburb: new FormControl('', Validators.required),
      postCode: new FormControl('', Validators.required),
      region: new FormControl(''),
      country: new FormControl(null, Validators.required),
      durationMonths: new FormControl(null, durationValidators),
      livingSituation: new FormControl(null, livingSituationValidators),
    })
  }



  /**
   * Converts an amount and frequency to a monthly value.
   */
  toMonthly(amount: number | null, frequency: PeriodTypeEnum | null): number {
    if (!amount || !frequency) return 0;
    switch (frequency) {
      case PeriodTypeEnum.Weekly:
        return amount * 52 / 12;
      case PeriodTypeEnum.Fortnightly:
        return amount * 26 / 12;
      case PeriodTypeEnum.Monthly:
        return amount;
      case PeriodTypeEnum.Quarterly:
        return amount / 3;
      case PeriodTypeEnum.Annually:
        return amount / 12;
      default:
        return 0;
    }
  }

  /**
   * Converts an amount and frequency to an annual value.
   */
  toAnnual(amount: number | null, frequency: PeriodTypeEnum | null): number {
    if (!amount || !frequency) return 0;
    switch (frequency) {
      case PeriodTypeEnum.Weekly:
        return amount * 52;
      case PeriodTypeEnum.Fortnightly:
        return amount * 26;
      case PeriodTypeEnum.Monthly:
        return amount * 12;
      case PeriodTypeEnum.Quarterly:
        return amount * 4;
      case PeriodTypeEnum.Annually:
        return amount;
      default:
        return 0;
    }
  }
}





